import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, TShirt, Sneaker, Trophy, Tag, Palette, Watch, X, Star, User as UserIcon, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};

const TYPES = [
  { id: "pala",         label: "Palas",         icon: Trophy },
  { id: "apparel",      label: "Ropa",          icon: TShirt },
  { id: "shoes",        label: "Calzado",       icon: Sneaker },
  { id: "wristband",    label: "Muñequeras",    icon: Watch },
  { id: "accessory",    label: "Accesorios",    icon: Tag },
  { id: "color",        label: "Colores",       icon: Palette },
  { id: "sponsor",      label: "Patrocinador",  icon: Star },
  { id: "avatar_brand", label: "Marcas AR 3D",  icon: UserIcon },
];

const EMPTY = { type: "pala", name: "", color: "#0A0A0A", text: "#FFFFFF", model: "", logo: "", short: "", active: true, sort: 0 };

export default function AdminGearCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("pala");
  const [dialog, setDialog] = useState(null); // "create" | { mode:"edit", item }
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const onLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Máximo 2MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/gear-catalog/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo: data.url }));
      toast.success("Logo subido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/gear-catalog")
      .then(({ data }) => {
        const flat = [];
        for (const [type, arr] of Object.entries(data || {})) {
          for (const it of arr || []) flat.push({ ...it, type });
        }
        setItems(flat);
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/dashboard"); return; }
    load();
  }, [user, navigate]);

  const byType = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => it.type === activeType)
      .filter((it) => !q || (it.name || "").toLowerCase().includes(q) || (it.model || "").toLowerCase().includes(q))
      .sort((a, b) => (a.sort || 0) - (b.sort || 0) || (a.name || "").localeCompare(b.name || ""));
  }, [items, activeType, search]);

  const counts = useMemo(() => {
    const m = {};
    for (const t of TYPES) m[t.id] = items.filter((it) => it.type === t.id).length;
    return m;
  }, [items]);

  const openCreate = () => { setForm({ ...EMPTY, type: activeType }); setDialog("create"); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setDialog({ mode: "edit", item: it }); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Ingresa un nombre"); return; }
    try {
      if (dialog === "create") await api.post("/admin/gear-catalog", form);
      else await api.put(`/admin/gear-catalog/${dialog.item.id}`, form);
      toast.success(dialog === "create" ? "Item creado" : "Item actualizado");
      setDialog(null);
      load();
      window.dispatchEvent(new Event("gear-catalog:changed"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}" definitivamente?`)) return;
    try {
      await api.delete(`/admin/gear-catalog/${it.id}`);
      toast.success("Eliminado");
      load();
      window.dispatchEvent(new Event("gear-catalog:changed"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/gear-catalog/${it.id}`, { active: !it.active });
      load();
      window.dispatchEvent(new Event("gear-catalog:changed"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-gear-catalog">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">Admin · Catálogo</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">Marcas y accesorios de jugadores</h1>
          <p className="text-xs text-gray-500 mt-1">Este catálogo aparece como opciones en <b>Mi Equipamiento</b> del perfil de cada jugador.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide" data-testid="gear-catalog-new">
          <Plus size={16} weight="bold" className="mr-1" /> Nuevo item
        </Button>
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3" data-testid="gear-catalog-tabs">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const on = activeType === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveType(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border transition-colors ${on ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
              data-testid={`gear-tab-${t.id}`}
            >
              <Icon size={14} weight={on ? "fill" : "regular"} />
              {t.label}
              <span className={`text-[9px] px-1.5 py-0.5 rounded ${on ? "bg-black text-[#CCFF00]" : "bg-white/5"}`}>{counts[t.id] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <Input
        placeholder="Buscar por marca o modelo…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="bg-[#0A0A0A] border-white/20 text-white h-10 max-w-sm focus:border-[#CCFF00]"
        data-testid="gear-catalog-search"
      />

      {/* Grid of chips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="gear-catalog-list">
        {byType.length === 0 ? (
          <p className="text-xs text-gray-500 italic col-span-full">Sin items en esta categoría. Añade el primero con "Nuevo item".</p>
        ) : (
          byType.map((it) => (
            <div
              key={it.id}
              className={`card-tactical p-3 border transition-colors ${it.active ? "border-white/10" : "border-red-500/25 opacity-60"}`}
              data-testid={`gear-item-${it.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <div
                  className="w-10 h-10 shrink-0 rounded flex items-center justify-center border border-white/10 font-['Barlow_Condensed'] font-black text-sm uppercase overflow-hidden"
                  style={{ backgroundColor: it.color || "#0A0A0A", color: it.text || "#FFFFFF" }}
                >
                  {it.logo ? (
                    <img src={absoluteUrl(it.logo)} alt={it.name} className="w-full h-full object-contain p-0.5" />
                  ) : (it.short || it.name || "?").slice(0, 4)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate" title={it.name}>{it.name}</p>
                  {it.model && <p className="text-[10px] text-gray-500 truncate">{it.model}</p>}
                  <div className="flex items-center gap-1 mt-0.5">
                    <Badge variant="outline" className="text-[9px] uppercase h-4 px-1.5 border-white/10 text-gray-400">{it.type}</Badge>
                    {!it.active && <Badge variant="outline" className="text-[9px] uppercase h-4 px-1.5 border-red-500/30 text-red-400">oculto</Badge>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(it)} className="h-7 px-2 text-[10px] uppercase text-[#CCFF00]" data-testid={`gear-edit-${it.id}`}>
                  <PencilSimple size={11} className="mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggleActive(it)} className="h-7 px-2 text-[10px] uppercase text-gray-400" data-testid={`gear-toggle-${it.id}`}>
                  {it.active ? "Ocultar" : "Mostrar"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(it)} className="h-7 px-2 text-[10px] uppercase text-red-400 ml-auto" data-testid={`gear-del-${it.id}`}>
                  <Trash size={11} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md" data-testid="gear-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
              {dialog === "create" ? "Nuevo item" : "Editar item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase mb-1 block">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-9 text-xs" data-testid="gear-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Orden</Label>
                <Input type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: parseInt(e.target.value || "0") })} className="bg-[#0A0A0A] border-white/20 text-white h-9 text-xs" data-testid="gear-form-sort" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase mb-1 block">Nombre / Marca</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#CCFF00]" placeholder="Ej: Bullpadel" data-testid="gear-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase mb-1 block">Modelo (opcional)</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-10" placeholder="Ej: Vertex 04 Control" data-testid="gear-form-model" />
            </div>
            {(form.type === "sponsor" || form.type === "avatar_brand" || form.type === "pala" || form.type === "apparel" || form.type === "shoes" || form.type === "wristband" || form.type === "accessory") && (
              <>
                <div>
                  <Label className="text-xs uppercase mb-1 block">Logo (URL o subir archivo)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={form.logo || ""}
                      onChange={(e) => setForm({ ...form, logo: e.target.value })}
                      className="bg-[#0A0A0A] border-white/20 text-white h-10 text-xs flex-1"
                      placeholder="https://…/mi-logo.png"
                      data-testid="gear-form-logo"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="h-10 border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[10px] uppercase font-bold shrink-0"
                      data-testid="gear-form-logo-upload"
                    >
                      <UploadSimple size={14} className="mr-1" /> {uploading ? "Subiendo…" : "Subir"}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={onLogoFileChange}
                      className="hidden"
                      data-testid="gear-form-logo-file"
                    />
                  </div>
                  {form.logo && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded">
                      <img
                        src={absoluteUrl(form.logo)}
                        alt="preview"
                        className="w-12 h-12 object-contain bg-white/5 rounded"
                        onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-gray-400 truncate">{form.logo}</p>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setForm({ ...form, logo: "" })}
                          className="h-6 px-2 text-[9px] text-red-400 uppercase mt-1"
                          data-testid="gear-form-logo-clear"
                        >
                          <X size={10} className="mr-1" /> Quitar
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500 mt-1">Se muestra en el badge del avatar 3D (pecho, pala, short, tenis, gorra) y en la ficha del jugador. Formato PNG/JPG/WEBP/SVG cuadrado ≤ 2MB. Ideal: fondo transparente.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase mb-1 block">Código corto AR (2-4 letras)</Label>
                  <Input
                    value={form.short || ""}
                    onChange={(e) => setForm({ ...form, short: e.target.value.slice(0, 4).toUpperCase() })}
                    className="bg-[#0A0A0A] border-white/20 text-white h-10 uppercase tracking-widest"
                    placeholder="DYD"
                    data-testid="gear-form-short"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Fallback cuando no hay logo (badge textual del casco/gorra en el avatar).</p>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase mb-1 block">Color de fondo</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={form.color || "#0A0A0A"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-14 border-white/20 p-0 bg-transparent cursor-pointer" data-testid="gear-form-color" />
                  <Input value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-9 text-xs flex-1" placeholder="#RRGGBB" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase mb-1 block">Color de texto</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={form.text || "#FFFFFF"} onChange={(e) => setForm({ ...form, text: e.target.value })} className="h-9 w-14 border-white/20 p-0 bg-transparent cursor-pointer" data-testid="gear-form-text" />
                  <Input value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white h-9 text-xs flex-1" placeholder="#RRGGBB" />
                </div>
              </div>
            </div>
            {/* Preview chip */}
            <div className="pt-2">
              <Label className="text-xs uppercase mb-1 block text-gray-500">Vista previa</Label>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 rounded font-['Barlow_Condensed'] font-black uppercase tracking-wide"
                style={{ backgroundColor: form.color || "#0A0A0A", color: form.text || "#FFFFFF" }}
                data-testid="gear-form-preview"
              >
                {form.logo && (
                  <img src={absoluteUrl(form.logo)} alt="preview" className="w-6 h-6 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                {form.name || "Sin nombre"}
                {form.short && <span className="text-[9px] tracking-widest opacity-70">· {form.short}</span>}
              </div>
            </div>
            <label className="flex items-center gap-2 pt-1 cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="gear-form-active" />
              <span className="text-xs text-gray-300">Activo (visible para jugadores)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-gray-400"><X size={14} className="mr-1" /> Cancelar</Button>
            <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="gear-form-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
